import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d69e6772-669a-4c5a-9337-e48f09c8c595/artifacts/dosqwhmo_361589719_248985681240088_4755260591056983724_n.jpg";

const TikTokIcon = ({ size = 18 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.23V2h-3.18v12.542a2.89 2.89 0 0 1-5.77 0 2.89 2.89 0 0 1 2.89-2.89c.286 0 .56.044.818.123V8.533a6.067 6.067 0 0 0-.818-.055A6.068 6.068 0 0 0 3.69 14.55a6.068 6.068 0 0 0 6.069 6.068 6.068 6.068 0 0 0 6.068-6.068V8.198a7.95 7.95 0 0 0 4.773 1.598V6.615c-.345.037-.696.061-1.011.071Z" />
  </svg>
);

const footerLinks = {
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Work', path: '/work' },
    { name: 'Contact', path: '/contact' },
  ],
  services: [
    { name: 'Social Media Management', path: '/services#social-media' },
    { name: 'Content Strategy', path: '/services#content-strategy' },
    { name: 'Email Marketing', path: '/services#email-marketing' },
    { name: 'Affiliate Marketing', path: '/services#affiliate-marketing' },
    { name: 'Ads Management', path: '/services#ads-management' },
  ],
};

const socialLinks = [
  { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/warner_digital_/' },
  { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/WarnerDigital1' },
  { name: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/company/warnerdigital' },
  { name: 'TikTok', icon: TikTokIcon, url: 'https://www.tiktok.com/@warner.digital' },
];

export const Footer = () => {
  return (
    <footer className="bg-forest text-cream" data-testid="footer">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <img 
                src={LOGO_URL} 
                alt="Warner Digital" 
                className="h-14 w-14 rounded-full object-cover"
              />
            </Link>
            <p className="text-cream/70 mb-6 leading-relaxed">
              Powering your online presence with data-driven strategies that deliver measurable results.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-cream/10 flex items-center justify-center hover:bg-gold hover:text-forest-dark transition-colors"
                  aria-label={social.name}
                  data-testid={`footer-social-${social.name.toLowerCase()}`}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6 text-gold">Navigation</h4>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-cream/70 hover:text-gold transition-colors"
                    data-testid={`footer-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6 text-gold">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-cream/70 hover:text-gold transition-colors"
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6 text-gold">Get In Touch</h4>
            <div className="space-y-4 text-cream/70">
              <p>Ready to elevate your digital presence?</p>
              <Link
                to="/contact"
                className="inline-block bg-gold text-forest-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light transition-colors"
                data-testid="footer-contact-cta"
              >
                Start a Project
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-cream/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-cream/50 text-sm">
            © {new Date().getFullYear()} Warner Digital Media. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-cream/50">
            <Link to="/privacy-policy" className="hover:text-gold transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-gold transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
